import { SPORTSNEW_LIST_STATUS, buildSportsnewAcceptedListGeneration, classifySportsnewListResponse, normalizeSportsnewListPresentation, canRecoverSportsnewPresentation, shouldRetrySportsnewList, sportsnewListRequestKey, sportsnewListRetryDelayMs } from '../_helpers/sportsnewListRequestState';
import { applySportsnewListRealtimeSince, recordSportsnewRestOdds } from '../_helpers/sportsnewDetailRealtime';
import { continueSportsnewListRefreshAfterFlight } from '../_helpers/sportsnewListFlight';
import { fxId, mapSportsnewFixtureOdds, applySportsnewPresentationReplayRows, reconcileSportsnewListRecoveryRows } from '../_helpers/sportsnewListPatch';
import { isListFilterModeScopeReady, listSubscriptionChanged, normalizedListSubscription, sportSlugOf, subscriptionValue } from '../_helpers/sportsnewContextConfig';
import { markListLoaded } from '../_helpers/sportsnewPerf';
import { normalizeSportsnewMembershipRevision } from '../_helpers/sportsnewMembershipRevision';
import { reconcileOperatorSuspensionsFromListSnapshot } from '../_helpers/sportsnewOperatorVisibility';
import { shouldReleaseSportsnewMembershipQuarantine, sportsnewMembershipQuarantineTargetsBoundary } from '../_helpers/sportsnewMembershipQuarantine';
import { sportsnewListGenerationDomain, sportsnewRequestSequenceSatisfiesMembershipBoundary } from '../_helpers/sportsnewMembershipRefreshCoordinator';
import { useCallback } from 'react';

// Owns list data within the existing split context.
// Provider refs retain account/list/detail authority; callers receive stable actions.
export const useSportsnewListData = ({
  frameDeliveryRef,
  SportsnewService, activeListRequestRef, authoritativeListSnapshotRef, commitFixtures,
  interruptedListRequestRef,
  detailRealtimeLedgerRef, displayScope, displayScopeRef, failCloseQuarantinedFixtures,
  fetchFixturesRef, filterHashRef, fixturesRef, fixturesRequestSeqRef,
  listFilterKeyRef, listLastGoodByKeyRef, listMembershipRevisionRef, listRequestsInFlightRef, listSequenceRef,
  listSubscriptionRef, listPresentationRef, listStreamRef, presentationRecoveryRef,
  membershipFailClosedFixturesRef, membershipRefreshCoordinatorRef, mode,
  modeRef, page, pageRef, pageSize,
  pageSizeRef, recoverFixtureDetailSingleFlight, selectedGameTitleId, selectedGameTitleIdRef,
  selectedLeagueId, selectedLeagueIdRef, selectedSport, selectedSportRef,
  selectedTournamentId, selectedTournamentIdRef, sessionOwnerRef, setError,
  setListMembershipRevision, setListSnapshotComplete, setListStatus, setListTotalItems,
  setLoading, setOperatorSuspendedFixtureIds, setResolvedListFilterKey, setTotalPages,
  socketGenerationRef, socketRef, subscribeListWithGenerationFence,
}) => {
  // A gap can borrow one shared GET. Later retries require a new WS input
  // after cooldown; idle views never poll and fixture Writers are not involved.
  const requestPresentationListRecovery = useCallback((eventDriven = false) => {
    const requestKey = listFilterKeyRef.current;
    const key = `${requestKey}|${socketGenerationRef.current}|${sessionOwnerRef.current}`;
    const now = Date.now();
    if (listRequestsInFlightRef.current.has(requestKey)
      || !canRecoverSportsnewPresentation({ previous: presentationRecoveryRef.current, key, now, eventDriven })) return;
    const refresh = fetchFixturesRef.current;
    if (typeof refresh !== 'function') return;
    presentationRecoveryRef.current = { key, startedAt: now };
    Promise.resolve(refresh({ bypassCache: true, automaticRecovery: true })).catch(() => {});
  }, [sessionOwnerRef, listFilterKeyRef, socketGenerationRef, listRequestsInFlightRef, presentationRecoveryRef, fetchFixturesRef]);

  const acceptListSnapshot = useCallback((res, params, requestFlight) => {
    frameDeliveryRef?.current?.flush();
      const requestKey = sportsnewListRequestKey(params);
      const classification = classifySportsnewListResponse(res);
      if (classification.status === SPORTSNEW_LIST_STATUS.ERROR) return null;
      if (!sportsnewRequestSequenceSatisfiesMembershipBoundary(
        requestFlight.sequence, membershipRefreshCoordinatorRef.current.state().requiredRequestSequence,
      )) return null;
      const data = res?.data;
      const presentation = data?.presentation == null ? null : normalizeSportsnewListPresentation(data, params);
      if (data?.presentation != null && !presentation) return null;
      const dedupMap = new Map();
      for (const f of classification.rows) {
        const id = fxId(f);
        if (id != null && !dedupMap.has(id)) dedupMap.set(id, f);
      }
      let rawNextFixtures = presentation
        ? applySportsnewPresentationReplayRows(Array.from(dedupMap.values()), presentation.deltas)
        : Array.from(dedupMap.values());
      const stream = listStreamRef.current;
      if (presentation?.cursor && stream?.room === presentation.room
        && stream.epoch === presentation.cursor.epoch && stream.socketGeneration === socketGenerationRef.current) {
        rawNextFixtures = reconcileSportsnewListRecoveryRows(rawNextFixtures, stream.fields,
          presentation.cursor.sequence, fixturesRef.current);
      }
      const membershipRevision = normalizeSportsnewMembershipRevision(data);
      const responseFixtureIds = new Set(rawNextFixtures.map(fxId).filter(Boolean));
      membershipFailClosedFixturesRef.current.forEach((quarantine, fixtureId) => {
        if (shouldReleaseSportsnewMembershipQuarantine({
          quarantine,
          fixtureId,
          requestKey,
          membershipRevision,
          responseFixtureIds,
          mode: params.mode,
          scope: params.scope,
        })) {
          membershipFailClosedFixturesRef.current.delete(String(fixtureId));
        }
      });
      const membershipSettled = ![...membershipFailClosedFixturesRef.current.values()]
        .some(quarantine => sportsnewMembershipQuarantineTargetsBoundary(quarantine, {
          requestKey,
          mode: params.mode,
          scope: params.scope,
        }));
      const ambiguousByFixture=new Map();
      const nextFixtures = failCloseQuarantinedFixtures(applySportsnewListRealtimeSince(
        rawNextFixtures, detailRealtimeLedgerRef.current, requestFlight.realtimeRevision, fixturesRef.current,
        {presentation,requireWriterForUnlock:!presentation&&params.mode==='live',onAmbiguous:(id,fixtureId)=>{
          if(!ambiguousByFixture.has(fixtureId))ambiguousByFixture.set(fixtureId,[]);
          ambiguousByFixture.get(fixtureId).push(id);
        }},
      ));
      const restOdds = [];
      nextFixtures.forEach(fixture => mapSportsnewFixtureOdds(fixture, odd => { restOdds.push(odd); return odd; }));
      if (!presentation) recordSportsnewRestOdds(detailRealtimeLedgerRef.current, restOdds, requestFlight.realtimeRevision);
      const acceptedGeneration = buildSportsnewAcceptedListGeneration({
        fixtures: nextFixtures,
        data,
        response: res,
        params,
        pageFallback: params.page,
        pageSizeFallback: params.limit,
        membershipRevision,
      });
      const acceptedGenerationDomain = sportsnewListGenerationDomain({
        mode: params.mode,
        sport: params.sport,
      });

      setOperatorSuspendedFixtureIds(prev => (
        reconcileOperatorSuspensionsFromListSnapshot(prev, acceptedGeneration.fixtures)
      ));
      commitFixtures(acceptedGeneration.fixtures);
      ambiguousByFixture.forEach((ids,fixtureId)=>recoverFixtureDetailSingleFlight(fixtureId,{snapshot:true,focusOddStateIds:ids}));
      setResolvedListFilterKey(acceptedGeneration.requestKey);
      setListStatus(classification.status);
      listMembershipRevisionRef.current = acceptedGeneration.membershipRevision;
      setListMembershipRevision(acceptedGeneration.membershipRevision);
      authoritativeListSnapshotRef.current = Object.freeze({
        requestKey: acceptedGeneration.requestKey,
        listCacheVersion: acceptedGeneration.listCacheVersion,
        generationDomain: acceptedGenerationDomain,
      });
      setTotalPages(acceptedGeneration.totalPages);
      setListTotalItems(acceptedGeneration.totalItems);
      setListSnapshotComplete(acceptedGeneration.snapshotComplete);
      listLastGoodByKeyRef.current.set(acceptedGeneration.requestKey, {
        sessionOwner: sessionOwnerRef.current,
        fixtures: acceptedGeneration.fixtures,
        totalPages: acceptedGeneration.totalPages,
        totalItems: acceptedGeneration.totalItems,
        membershipRevision: acceptedGeneration.membershipRevision,
        snapshotComplete: acceptedGeneration.snapshotComplete,
      });
      markListLoaded();
      membershipRefreshCoordinatorRef.current.markResolved({
        listCacheVersion: acceptedGeneration.listCacheVersion,
        membershipRevision: acceptedGeneration.membershipRevision,
        membershipSettled,
        requestSequence: requestFlight.sequence,
        generationDomain: acceptedGenerationDomain,
      });

      if (presentation) {
        if (!presentation.gap) presentationRecoveryRef.current = null;
        const previous = listStreamRef.current;
        const sameStream = previous?.room === presentation.room
          && previous?.epoch === presentation.cursor?.epoch
          && previous?.socketGeneration === socketGenerationRef.current;
        listPresentationRef.current = { ...presentation, requestKey,
          socketGeneration: socketGenerationRef.current };
        if (presentation.cursor) listSequenceRef.current = sameStream
          ? Math.max(listSequenceRef.current ?? 0, presentation.cursor.sequence)
          : presentation.cursor.sequence;
        listStreamRef.current = { room: presentation.room, epoch: presentation.cursor?.epoch,
          socketGeneration: socketGenerationRef.current,
          fields: sameStream ? previous.fields : { byFixture: new Map(), invalid: false } };
      } else listPresentationRef.current = null;
      return { acceptedGeneration, membershipSettled, acceptedGenerationDomain, presentationGap: presentation?.gap === true };
    }, [
    authoritativeListSnapshotRef, commitFixtures, detailRealtimeLedgerRef,
    failCloseQuarantinedFixtures, fixturesRef, listLastGoodByKeyRef, listMembershipRevisionRef,
    membershipFailClosedFixturesRef, membershipRefreshCoordinatorRef,
    recoverFixtureDetailSingleFlight, setListMembershipRevision, setListSnapshotComplete,
    setListStatus, setListTotalItems, setOperatorSuspendedFixtureIds, setResolvedListFilterKey,
    setTotalPages, sessionOwnerRef, frameDeliveryRef, listPresentationRef, listStreamRef, presentationRecoveryRef,
    listSequenceRef, socketGenerationRef,
  ]);

  const fetchFixtures = useCallback((opts = {}) => {
      if (!SportsnewService) return;
      const refreshOwner = membershipRefreshCoordinatorRef.current;
      const params = {
        mode: opts.mode ?? mode,
        scope: opts.scope ?? displayScope,
        page: opts.page ?? page,
        limit: pageSize,
      };

      const sport = opts.sport !== undefined ? opts.sport : selectedSport;
      const sportSlug = sport ? (typeof sport === 'object' ? sport.slug : sport) : null;
      if (sportSlug) params.sport = sportSlug;

      const leagueId = opts.league_id !== undefined ? opts.league_id : selectedLeagueId;
      if (leagueId) params.league_id = leagueId;

      const isEsportsFilter = String(sportSlug || '').toLowerCase() === 'esports' || params.scope === 'esports';

      const gameTitleId = opts.game_title_id !== undefined ? opts.game_title_id : selectedGameTitleId;
      if (isEsportsFilter && gameTitleId) params.game_title_id = gameTitleId;

      const tournamentId = opts.tournament_id !== undefined ? opts.tournament_id : selectedTournamentId;
      if (isEsportsFilter && tournamentId) params.tournament_id = tournamentId;

      if (!isListFilterModeScopeReady(params)) return;

      const requestKey = sportsnewListRequestKey(params);
      // Delayed callers may retain a previous view's refresh closure.
      // Reject it before changing current recovery or request ownership.
      if (requestKey !== listFilterKeyRef.current) return;
      if (opts.automaticRecovery === true && refreshOwner.state().terminalBlocked) {
        return Promise.resolve({ recoveryOutcome: 'terminal' });
      }
      if (opts.automaticRecovery !== true) refreshOwner.resumeExplicitly();

      const existingFlight = listRequestsInFlightRef.current.get(requestKey);
      if (existingFlight && !existingFlight.cancelled) {
        if (opts.afterCurrentFlight === true) {
          return continueSportsnewListRefreshAfterFlight(existingFlight, () => {
            if (listRequestsInFlightRef.current.get(requestKey) === existingFlight) {
              listRequestsInFlightRef.current.delete(requestKey);
            }
            const refresh = fetchFixturesRef.current;
            return typeof refresh === 'function'
              ? refresh({ bypassCache: true, automaticRecovery: opts.automaticRecovery === true })
              : undefined;
          });
        }
        activeListRequestRef.current = existingFlight;
        return existingFlight.promise;
      }

      frameDeliveryRef?.current?.flush();
    const requestFlight = {
        key: requestKey,
        sequence: ++fixturesRequestSeqRef.current,
        realtimeRevision: detailRealtimeLedgerRef.current.revision,
        socketGeneration: socketGenerationRef.current,
        sessionOwner: sessionOwnerRef.current,
        cancelled: false,
        retryWait: null,
        abortController: new AbortController(),
        promise: null,
      };
      activeListRequestRef.current = requestFlight;

      const isCurrentRequest = () => (
        !requestFlight.cancelled
        && requestFlight.sessionOwner === sessionOwnerRef.current
        && requestFlight.socketGeneration === socketGenerationRef.current
        && activeListRequestRef.current === requestFlight
        && subscriptionValue(params.mode) === subscriptionValue(modeRef.current)
        && subscriptionValue(params.scope) === subscriptionValue(displayScopeRef.current)
        && subscriptionValue(params.sport) === sportSlugOf(selectedSportRef.current)
        && subscriptionValue(params.league_id) === subscriptionValue(selectedLeagueIdRef.current)
        && subscriptionValue(params.game_title_id) === subscriptionValue(selectedGameTitleIdRef.current)
        && subscriptionValue(params.tournament_id) === subscriptionValue(selectedTournamentIdRef.current)
        && Number(params.page || 1) === Number(pageRef.current || 1)
        && Number(params.limit || pageSizeRef.current || pageSize) === Number(pageSizeRef.current || pageSize)
      );

      setLoading(true);
      setError(null);
      setListStatus(SPORTSNEW_LIST_STATUS.LOADING);

      const requestPromise = (async () => {
        let retryCount = 0;
        let res;
        let classification;

        try {
          do {
            frameDeliveryRef?.current?.flush();
            requestFlight.realtimeRevision = detailRealtimeLedgerRef.current.revision;
            res = await SportsnewService.getFixtures(params, retryCount > 0 || opts.bypassCache === true ? {
              cacheBuster: `${Date.now()}-${requestFlight.sequence}`,
              signal: requestFlight.abortController.signal,
            } : { signal: requestFlight.abortController.signal });
            if (!isCurrentRequest()) return;
            const { requiredRequestSequence } = membershipRefreshCoordinatorRef.current.state();
            if (!sportsnewRequestSequenceSatisfiesMembershipBoundary(
              requestFlight.sequence,
              requiredRequestSequence,
            )) return;

            classification = classifySportsnewListResponse(res);
            if (!shouldRetrySportsnewList(classification, retryCount)) break;

            retryCount += 1;
            const retryDelayMs = sportsnewListRetryDelayMs(res);
            if (retryDelayMs > 0) {
              const shouldContinue = await new Promise((resolve) => {
                const pending = { resolve, timer: null };
                pending.timer = setTimeout(() => {
                  if (requestFlight.retryWait === pending) requestFlight.retryWait = null;
                  resolve(true);
                }, retryDelayMs);
                requestFlight.retryWait = pending;
              });
              if (!shouldContinue || !isCurrentRequest()) return;
            }
          } while (retryCount <= 1);

          if (classification?.status === SPORTSNEW_LIST_STATUS.ERROR) {
            const message = classification.reason === 'transition'
              ? '라이브 경기 목록이 갱신 중입니다. 다시 시도해 주세요.'
              : (res?.message || '경기 목록을 불러오지 못했습니다.');
            setError(message);
            setListStatus(SPORTSNEW_LIST_STATUS.ERROR);
            return {
              recoveryOutcome: classification.retryable ? 'retryable' : 'terminal',
              retryAfterMs: sportsnewListRetryDelayMs(res),
            };
          }

          const accepted = acceptListSnapshot(res, params, requestFlight);
          if (!accepted) return;
          interruptedListRequestRef.current = null;
          const { acceptedGeneration, membershipSettled, acceptedGenerationDomain } = accepted;
          const data = res?.data;
          const hash = (data && !Array.isArray(data) ? data.subscription?.filterHash : undefined)
            ?? res?.subscription?.filterHash;

          if (hash) {
            filterHashRef.current = hash;
            const echoed = data?.subscription ?? {};
            const sub = normalizedListSubscription({
              echoed,
              params,
              hash,
              pageFallback: page,
              limitFallback: pageSize,
            });
            const prevSub = listSubscriptionRef.current;
            const subChanged = listSubscriptionChanged(prevSub, sub);
            listSubscriptionRef.current = sub;
            if (subChanged) listSequenceRef.current = listPresentationRef.current?.requestKey === requestKey
              ? listPresentationRef.current.cursor?.sequence ?? null : null;

            if (socketRef.current?.connected && subChanged) {
              if (prevSub) socketRef.current.emit('unsubscribe:sportsnew:list', prevSub);
              subscribeListWithGenerationFence(socketRef.current, sub, {
                requiredRequestSequence: requestFlight.sequence + 1,
              });
            }
          } else {
            console.warn('[sportsnew] no filterHash in fixtures response — socket list updates disabled');
          }
          return {
            listCacheVersion: acceptedGeneration.listCacheVersion,
            membershipRevision: acceptedGeneration.membershipRevision,
            membershipSettled,
            requestSequence: requestFlight.sequence,
            generationDomain: acceptedGenerationDomain,
            presentationGap: accepted.presentationGap,
          };
        } catch (err) {
          if (!isCurrentRequest()) return;
          console.error('[sportsnew] fetchFixtures error:', err);
          setError(err?.message || '경기 목록을 불러오지 못했습니다.');
          setListStatus(SPORTSNEW_LIST_STATUS.ERROR);
          return { recoveryOutcome: 'terminal' };
        } finally {
          if (isCurrentRequest()) setLoading(false);
        }
      })();

      requestFlight.promise = requestPromise;
      listRequestsInFlightRef.current.set(requestKey, requestFlight);
      requestPromise.then((result) => {
        const current = listRequestsInFlightRef.current.get(requestKey);
        if (current === requestFlight) listRequestsInFlightRef.current.delete(requestKey);
        if (!isCurrentRequest()) return;
        const coordinator = membershipRefreshCoordinatorRef.current;
        if (result?.presentationGap) requestPresentationListRecovery();
        if (result?.recoveryOutcome === 'terminal') coordinator.markTerminal();
        else if (result?.recoveryOutcome === 'retryable') {
          coordinator.ensureRecovery(
            requestFlight.sequence + 1, sportsnewListGenerationDomain(params), result.retryAfterMs,
          );
        }
      });
      return requestPromise;
    }, [
    SportsnewService, membershipRefreshCoordinatorRef, mode, displayScope, page, pageSize,
    selectedSport, selectedLeagueId, selectedGameTitleId, selectedTournamentId,
    listFilterKeyRef, listRequestsInFlightRef, fixturesRequestSeqRef, detailRealtimeLedgerRef, socketGenerationRef,
    sessionOwnerRef, activeListRequestRef, interruptedListRequestRef, setLoading, setError, setListStatus, fetchFixturesRef,
    modeRef, displayScopeRef, selectedSportRef, selectedLeagueIdRef, selectedGameTitleIdRef,
    selectedTournamentIdRef, pageRef, pageSizeRef, acceptListSnapshot, filterHashRef,
    listSubscriptionRef, listSequenceRef, socketRef, subscribeListWithGenerationFence,
    listPresentationRef, requestPresentationListRecovery,
  ]);

  return { acceptListSnapshot, fetchFixtures, requestPresentationListRecovery };
};
